import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Plus, Trash2, X, ChevronDown, ChevronUp, Search, Check, AlertCircle, User, Package, FileText, CreditCard, DollarSign, Edit3, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import CustomerSelectionModal from '../components/CustomerSelectionModal';
import EditCustomerModal from '../components/EditCustomerModal';
import SignatureModal from '../components/SignatureModal';
import { PurityPickerModal } from '../components/ModalPicker';
import { RatesSection } from '../components/RatesSection';
import SignatureCanvas from 'react-signature-canvas';
import { PaymentTypeSelector, CashPaymentModal, CardPaymentModal, UPIPaymentModal } from '../components/PaymentModals';
import PaymentReminderCard from '../components/PaymentReminderCard';
import { BankTransferModal, OldGoldModal } from '../components/PaymentModalsExtended';
import { MetalCreditModal } from '../components/MetalCreditModalNew';
import { PaymentSummary } from '../components/PaymentSummary';
import useExitConfirmation from '../hooks/useExitConfirmation';
import { uploadToCloudinary, uploadMultipleToCloudinary, isBase64DataUrl } from '../services/cloudinaryService';

const CreateBookingStepper = ({ user, onLogout, editMode = false, existingBooking = null }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [bookingTypes, setBookingTypes] = useState([]);
  const [signatureNames, setSignatureNames] = useState([]);
  
  // Customer
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  
  // Payment Reminder
  const [showPaymentReminder, setShowPaymentReminder] = useState(false);
  const [paymentReminderData, setPaymentReminderData] = useState(null);
  
  // Booking Items
  const [bookingItems, setBookingItems] = useState([]);
  
  // Reminder Date
  const [reminderDate, setReminderDate] = useState('');
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [showInternalRemark, setShowInternalRemark] = useState({});
  
  // Booking Type Modal
  const [showBookingTypeModal, setShowBookingTypeModal] = useState(false);
  const [bookingTypeModalTargetId, setBookingTypeModalTargetId] = useState(null);
  const [bookingTypeSearch, setBookingTypeSearch] = useState('');
  
  // Rates
  const [rateMode, setRateMode] = useState('single');
  const [ratePolicy, setRatePolicy] = useState('none');
  const [lockDate, setLockDate] = useState('');
  
  // GST and Tax settings
  const [taxScope, setTaxScope] = useState('TAXABLE');
  const [gstApplicable, setGstApplicable] = useState(true);
  const [gstInputStyle, setGstInputStyle] = useState('EXCL');
  const [gstPercent, setGstPercent] = useState(3.0);
  
  // Master Data from Settings
  const [purityOptions, setPurityOptions] = useState([]);
  const fallbackPurities = ['24K', '22K', '18K'];
  
  const [purityMultipliers, setPurityMultipliers] = useState({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
  
  // Rate data
  const [base24kRate, setBase24kRate] = useState('');
  const [purityRates, setPurityRates] = useState({});
  const [purityRateLinks, setPurityRateLinks] = useState({});
  const [itemRates, setItemRates] = useState({});
  
  // Booking Date
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
  const [pendingBookingDate, setPendingBookingDate] = useState('');
  
  // Review & Finalize
  const [showReviewPage, setShowReviewPage] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdBookingData, setCreatedBookingData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Inline Signatures (no modal)
  const [managementSignature, setManagementSignature] = useState(null);
  const [managementSignatureName, setManagementSignatureName] = useState('');
  const [customerSignature, setCustomerSignature] = useState(null);
  const [showManagementSignatureModal, setShowManagementSignatureModal] = useState(false);
  const [showCustomerSignatureModal, setShowCustomerSignatureModal] = useState(false);
  const managementSigRef = useRef(null);
  const customerSigRef = useRef(null);
  
  // Payment
  const [payments, setPayments] = useState([]);
  const [showPaymentTypeSelector, setShowPaymentTypeSelector] = useState(false);
  const [activePaymentModal, setActivePaymentModal] = useState(null);
  const [metalCreditBalance, setMetalCreditBalance] = useState(0);
  const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePaymentIndex, setDeletePaymentIndex] = useState(null);
  
  // Modal pickers state
  const [activeModal, setActiveModal] = useState(null);
  const [modalItemId, setModalItemId] = useState(null);
  
  // Section states
  const [expandedSections, setExpandedSections] = useState({
    items: true,
    rates: false,
    payment: false
  });
  
  const [loading, setLoading] = useState(false);
  
  // Refs for scrolling
  const itemsRef = useRef(null);
  const ratesRef = useRef(null);
  const paymentRef = useRef(null);

  // Exit confirmation - show warning when user has unsaved data
  const hasUnsavedChanges = !showSuccessModal && (
    selectedCustomer || 
    bookingItems.length > 0 || 
    payments.length > 0 ||
    managementSignature ||
    customerSignature
  );
  const { confirmNavigation } = useExitConfirmation(
    hasUnsavedChanges && !editMode,
    'You have unsaved booking data. Are you sure you want to leave? All entered information will be lost.'
  );

  useEffect(() => {
    loadCustomers();
    loadBookingTypes();
    loadGSTConfig();
    loadSignatureNames();
    
    // Auto-set management signature name to current user's username
    if (user?.username) {
      setManagementSignatureName(user.username);
    }
  }, []);
  
  // Populate data in edit mode
  useEffect(() => {
    if (editMode && existingBooking) {
      setSelectedCustomer(existingBooking.customer_id);
      // Transform items to have 'id' field for editing (database has 'item_id')
      const transformedItems = (existingBooking.items || []).map(item => ({
        ...item,
        id: item.id || item.item_id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }));
      setBookingItems(transformedItems);
      setReminderDate(existingBooking.reminder_date || '');
      setReminderEnabled(!!existingBooking.reminder_date);
      setRateMode(existingBooking.rate_mode || 'single');
      setRatePolicy(existingBooking.rate_policy || 'none');
      setLockDate(existingBooking.lock_date || '');
      setTaxScope(existingBooking.tax_scope || 'TAXABLE');
      setGstApplicable(existingBooking.gst_applicable !== false);
      setGstInputStyle(existingBooking.gst_input_style || 'EXCL');
      setGstPercent(existingBooking.gst_percent || 3.0);
      setBase24kRate(existingBooking.base24k_rate_ex_gst || '');
      setPurityRates(existingBooking.purity_rates || {});
      setPayments(existingBooking.advance_payments || []);
      setBookingDate(existingBooking.booking_date || existingBooking.created_at?.split('T')[0] || new Date().toISOString().split('T')[0]);
      
      // Pre-fill per-item rates in edit mode - remap from index-based keys to new item IDs
      if (existingBooking.item_rates && Object.keys(existingBooking.item_rates).length > 0) {
        const remappedItemRates = {};
        transformedItems.forEach((item, index) => {
          const itemKey = `item_${index}`;
          if (existingBooking.item_rates[itemKey]) {
            remappedItemRates[item.id] = existingBooking.item_rates[itemKey];
          }
        });
        setItemRates(remappedItemRates);
      } else {
        setItemRates({});
      }
      
      // Pre-fill signatures if they exist
      if (existingBooking.management_signature) {
        setManagementSignature(existingBooking.management_signature);
      }
      if (existingBooking.management_signature_name) {
        setManagementSignatureName(existingBooking.management_signature_name);
      }
      if (existingBooking.customer_signature) {
        setCustomerSignature(existingBooking.customer_signature);
      }
      
      // Load full reminder data if reminder_date exists
      if (existingBooking.reminder_date) {
        loadExistingReminder(existingBooking.booking_id);
      }
    }
  }, [editMode, existingBooking]);
  
  // Set default reminder date (next day) only if reminder is enabled
  useEffect(() => {
    if (bookingDate && reminderEnabled) {
      const nextDay = new Date(bookingDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setReminderDate(nextDay.toISOString().split('T')[0]);
    }
  }, [bookingDate, reminderEnabled]);

  const loadCustomers = async () => {
    try {
      const res = await axiosInstance.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error('Error loading customers:', err);
    }
  };

  const loadBookingTypes = async () => {
    try {
      const res = await axiosInstance.get('/booking-types');
      setBookingTypes(res.data);
      if (res.data.length === 0) {
        toast.info('No booking types found. Please contact admin to initialize.');
      }
    } catch (err) {
      console.error('Error loading booking types:', err);
      toast.error('Failed to load booking types');
    }
  };

  const loadGSTConfig = async () => {
    try {
      const [gstRes, purityRes] = await Promise.all([
        axiosInstance.get('/masters/gst-config'),
        axiosInstance.get('/masters/purity-options')
      ]);
      
      if (gstRes.data) {
        setGstPercent(gstRes.data.gst_percent || 3.0);
      }
      
      const activePurities = (purityRes.data || [])
        .filter(item => item.is_active !== false)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      
      if (activePurities.length > 0) {
        setPurityOptions(activePurities.map(p => p.name));
        const multipliers = {};
        activePurities.forEach(p => {
          multipliers[p.name] = p.multiplier;
        });
        setPurityMultipliers(multipliers);
      } else {
        setPurityOptions(fallbackPurities);
        setPurityMultipliers({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
      }
    } catch (err) {
      console.error('Error loading GST config:', err);
      setPurityOptions(fallbackPurities);
      setPurityMultipliers({ '24K': 1.0, '22K': 0.92, '18K': 0.76 });
    }
  };

  const loadSignatureNames = async () => {
    try {
      const res = await axiosInstance.get('/masters/signature-names');
      setSignatureNames(res.data);
    } catch (err) {
      console.error('Error loading signature names:', err);
      // Use defaults if API fails
      setSignatureNames([
        { id: 'sk', name: 'SK' },
        { id: 'mk', name: 'MK' },
        { id: 'av', name: 'AV' }
      ]);
    }
  };

  const loadExistingReminder = async (bookingId) => {
    try {
      const res = await axiosInstance.get('/reminders', {
        params: { order_id: bookingId }
      });
      if (res.data && res.data.length > 0) {
        const reminder = res.data[0];
        // Set payment reminder data to pre-fill the form
        setPaymentReminderData({
          enabled: true,
          due_at: reminder.due_at,
          amount_due: reminder.amount_due,
          channels: reminder.channels || [],
          reason_tags: reminder.reason_tags || [],
          notes: reminder.notes || '',
          auto_snooze: reminder.auto_snooze || null
        });
        // Expand payment reminder section when loading existing reminder
        setShowPaymentReminder(true);
        console.log('Loaded existing reminder for booking:', reminder);
      }
    } catch (err) {
      console.log('No existing reminder found for booking');
    }
  };

  // Auto-compute purity rates from base 24K rate
  const computePurityRates = (base24k) => {
    const computed = {};
    const links = {};
    Object.keys(purityMultipliers).forEach(purity => {
      if (purity !== '24K') {
        computed[purity] = Math.round(parseFloat(base24k) * purityMultipliers[purity]);
        links[purity] = true;
      }
    });
    setPurityRates(computed);
    setPurityRateLinks(links);
  };

  const handleBase24kChange = (value) => {
    setBase24kRate(value);
    if (value && !isNaN(value)) {
      computePurityRates(value);
    } else {
      setPurityRates({});
      setPurityRateLinks({});
    }
  };

  const togglePurityLink = (purity) => {
    setPurityRateLinks(prev => ({
      ...prev,
      [purity]: !prev[purity]
    }));
    
    if (!purityRateLinks[purity] && base24kRate) {
      setPurityRates(prev => ({
        ...prev,
        [purity]: Math.round(parseFloat(base24kRate) * purityMultipliers[purity])
      }));
    }
  };

  const handlePurityRateChange = (purity, value) => {
    setPurityRates(prev => ({
      ...prev,
      [purity]: value
    }));
    setPurityRateLinks(prev => ({
      ...prev,
      [purity]: false
    }));
  };

  const addBookingItem = (afterIndex = null) => {
    // Generate unique ID with timestamp + random to avoid collisions
    const newItemId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newItem = {
      id: newItemId,
      booking_type: 'Ornaments',
      weight: '',
      public_remark: '',
      internal_remark: '',
      collapsed: false
    };
    
    if (afterIndex !== null) {
      const newItems = [...bookingItems];
      newItems.splice(afterIndex + 1, 0, newItem);
      setBookingItems(newItems);
    } else {
      setBookingItems([...bookingItems, newItem]);
    }
    setExpandedItems({ ...expandedItems, [newItemId]: true });
  };

  const removeBookingItem = (id) => {
    setBookingItems(bookingItems.filter(item => item.id !== id));
    const newExpanded = { ...expandedItems };
    delete newExpanded[id];
    setExpandedItems(newExpanded);
  };

  const toggleItemExpand = (id) => {
    setExpandedItems({ ...expandedItems, [id]: !expandedItems[id] });
  };

  const collapseItem = (id) => {
    setExpandedItems({ ...expandedItems, [id]: false });
    const newItems = bookingItems.map(item =>
      item.id === id ? { ...item, collapsed: true } : item
    );
    setBookingItems(newItems);
  };

  const toggleSection = (section) => {
    setExpandedSections({ ...expandedSections, [section]: !expandedSections[section] });
  };

  const scrollToSection = (ref, section) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setExpandedSections({ ...expandedSections, [section]: true });
  };

  const updateBookingItem = (id, field, value) => {
    const newItems = bookingItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setBookingItems(newItems);
  };

  const toggleInternalRemark = (id) => {
    setShowInternalRemark({
      ...showInternalRemark,
      [id]: !showInternalRemark[id]
    });
  };

  // Booking Date Handling
  const handleBookingDateChange = (newDate) => {
    // Allow all users to set any date - admin will review via approval page
    setBookingDate(newDate);
  };

  // OTP handlers removed - backdating allowed without OTP

  // Payment Handlers
  const handlePaymentSave = (paymentData) => {
    if (editingPaymentIndex !== null) {
      const oldPayment = payments[editingPaymentIndex];
      const newPayments = [...payments];
      newPayments[editingPaymentIndex] = paymentData;
      setPayments(newPayments);
      
      if (oldPayment.mode === 'Metal Credit') {
        setMetalCreditBalance(metalCreditBalance - (oldPayment.fine_wt || 0));
      } else if (oldPayment.mode === 'Old Gold') {
        const oldFineWt = oldPayment.items?.reduce((sum, item) => sum + (parseFloat(item.fine_wt) || 0), 0) || 0;
        setMetalCreditBalance(metalCreditBalance - oldFineWt);
      }
      
      if (paymentData.mode === 'Metal Credit') {
        setMetalCreditBalance(metalCreditBalance + (paymentData.fine_wt || 0));
      } else if (paymentData.mode === 'Old Gold') {
        const newFineWt = paymentData.items?.reduce((sum, item) => sum + (parseFloat(item.fine_wt) || 0), 0) || 0;
        setMetalCreditBalance(metalCreditBalance + newFineWt);
      }
      
      setEditingPaymentIndex(null);
      toast.success('Payment updated!');
    } else {
      setPayments([...payments, paymentData]);
      
      if (paymentData.mode === 'Metal Credit') {
        setMetalCreditBalance(metalCreditBalance + (paymentData.fine_wt || 0));
      } else if (paymentData.mode === 'Old Gold') {
        const fineWt = paymentData.items?.reduce((sum, item) => sum + (parseFloat(item.fine_wt) || 0), 0) || 0;
        setMetalCreditBalance(metalCreditBalance + fineWt);
      }
      
      toast.success('Payment added!');
    }
    
    setActivePaymentModal(null);
    setShowPaymentTypeSelector(false);
  };

  const handlePaymentEdit = (payment, idx) => {
    setEditingPaymentIndex(idx);
    
    // Determine which modal to open based on payment mode
    const modalMap = {
      'Cash': 'cash',
      'Card': 'card',
      'UPI': 'upi',
      'IMPS': 'bank',
      'NEFT/RTGS': 'bank',
      'Cheque': 'bank',
      'Old Gold': 'old-gold',
      'Metal Credit': 'metal'
    };
    
    setActivePaymentModal(modalMap[payment.mode] || null);
  };

  const handlePaymentDelete = (index) => {
    setDeletePaymentIndex(index);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    const payment = payments[deletePaymentIndex];
    
    if (payment.mode === 'Metal Credit') {
      setMetalCreditBalance(metalCreditBalance - (payment.fine_wt || 0));
    } else if (payment.mode === 'Old Gold') {
      const fineWt = payment.items?.reduce((sum, item) => sum + (parseFloat(item.fine_wt) || 0), 0) || 0;
      setMetalCreditBalance(metalCreditBalance - fineWt);
    }
    
    setPayments(payments.filter((_, i) => i !== deletePaymentIndex));
    setShowDeleteConfirm(false);
    setDeletePaymentIndex(null);
    toast.success('Payment deleted');
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletePaymentIndex(null);
  };

  // Calculate Totals
  const calculateTotals = () => {
    let subtotal = 0;
    
    bookingItems.forEach(item => {
      // For bookings, we assume 24K purity (no purity field in bookings)
      const itemRate = rateMode === 'single' 
        ? parseFloat(base24kRate) || 0
        : parseFloat(itemRates[item.id] || 0);
      
      let calcRate = itemRate;
      if (gstInputStyle === 'EXCL' && itemRate > 0) {
        calcRate = itemRate * 1.03;
      }
      
      const weight = parseFloat(item.weight || 0);
      subtotal += weight * calcRate;
    });
    
    const advancePaid = payments
      .filter(p => !['Old Gold', 'Metal Credit'].includes(p.mode))
      .reduce((sum, p) => {
        const amount = parseFloat(p.amount) || parseFloat(p.inr) || 0;
        // Subtract if it's a refund, add if it's a payment
        return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
      }, 0);
    
    const metalAdvance = metalCreditBalance;
    const totalAdvance = advancePaid + metalAdvance;
    const balance = subtotal - totalAdvance;
    const advancePercent = subtotal > 0 ? ((totalAdvance / subtotal) * 100).toFixed(1) : 0;
    
    return { subtotal, advancePaid, metalAdvance, totalAdvance, balance, advancePercent };
  };

  // Validation
  const getSectionStatus = (section) => {
    if (section === 'items') {
      if (bookingItems.length === 0) return 'incomplete';
      const allValid = bookingItems.every(item => item.booking_type && item.weight);
      return allValid ? 'complete' : 'incomplete';
    }
    if (section === 'rates') {
      if (ratePolicy === 'none') return 'complete';
      if (rateMode === 'single') {
        return base24kRate ? 'complete' : 'incomplete';
      }
      return 'complete';
    }
    if (section === 'payment') {
      return payments.length > 0 ? 'complete' : 'warning';
    }
    return 'complete';
  };

  const getStatusIcon = (status) => {
    if (status === 'complete') return <Check className="w-4 h-4 text-green-600" />;
    if (status === 'warning') return <AlertCircle className="w-4 h-4 text-amber-600" />;
    return <AlertCircle className="w-4 h-4 text-red-600" />;
  };

  // Submit Handler
  const handleSubmit = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    
    if (bookingItems.length === 0) {
      toast.error('Please add at least one booking item');
      return;
    }
    
    // If editing and signatures already exist, skip signature modal
    // Submit directly with inline signatures (no modal)
    handleSignaturesComplete({
      customer_signature: customerSignature,
      management_signature: managementSignature,
      management_signature_name: managementSignatureName
    });
  };

  const handleSignaturesComplete = async (signatureData) => {
    try {
      setLoading(true);
      setIsSubmitting(true);  // Allow navigation after submit
      
      toast.info('Uploading data...', { duration: 2000 });
      
      // Collect signatures for batch upload
      const allBase64Photos = [];
      const photoMapping = [];
      
      let uploadedManagementSig = signatureData?.management_signature || existingBooking?.management_signature || null;
      let uploadedCustomerSig = signatureData?.customer_signature || existingBooking?.customer_signature || null;
      
      if (uploadedManagementSig && isBase64DataUrl(uploadedManagementSig)) {
        allBase64Photos.push(uploadedManagementSig);
        photoMapping.push({ type: 'managementSig' });
      }
      
      if (uploadedCustomerSig && isBase64DataUrl(uploadedCustomerSig)) {
        allBase64Photos.push(uploadedCustomerSig);
        photoMapping.push({ type: 'customerSig' });
      }
      
      // Upload all in parallel
      if (allBase64Photos.length > 0) {
        try {
          const results = await uploadMultipleToCloudinary(allBase64Photos, 'bookings');
          photoMapping.forEach((mapping, idx) => {
            if (mapping.type === 'managementSig') {
              uploadedManagementSig = results[idx].url;
            } else if (mapping.type === 'customerSig') {
              uploadedCustomerSig = results[idx].url;
            }
          });
        } catch (e) {
          console.error('Batch upload failed:', e);
        }
      }
      
      // Convert itemRates to item_rates for backend (per-item mode)
      let finalItemRates = {};
      let finalPurityRates = {};
      
      if (rateMode === 'per-item' && Object.keys(itemRates).length > 0) {
        // Map itemRates to item indices for persistence
        // Frontend uses temporary IDs, backend needs index-based keys
        bookingItems.forEach((item, index) => {
          if (itemRates[item.id]) {
            finalItemRates[`item_${index}`] = parseFloat(itemRates[item.id]);
          }
        });
        
        // Also populate purity_rates for backward compatibility
        bookingItems.forEach(item => {
          if (itemRates[item.id]) {
            finalPurityRates[item.booking_type] = parseFloat(itemRates[item.id]);
          }
        });
      } else {
        // Single rate mode - use purity_rates
        finalPurityRates = purityRates;
      }
      
      // In edit mode, start with existing booking data to preserve all fields
      const baseData = editMode && existingBooking ? { ...existingBooking } : {};
      
      const bookingData = {
        ...baseData, // Preserve all existing fields
        customer_id: selectedCustomer,
        booking_date: bookingDate,
        items: bookingItems.map(item => ({
          booking_type: item.booking_type,
          weight: parseFloat(item.weight),
          public_remark: item.public_remark || null,
          internal_remark: item.internal_remark || null
        })),
        rate_mode: rateMode,
        rate_policy: ratePolicy,
        lock_date: lockDate || existingBooking?.lock_date || null,
        tax_scope: taxScope,
        gst_applicable: gstApplicable,
        gst_input_style: gstInputStyle,
        gst_percent: gstPercent,
        base24k_rate_ex_gst: base24kRate ? parseFloat(base24kRate) : null,
        purity_rates: finalPurityRates,
        item_rates: finalItemRates,  // Added: per-item rates
        advance_payments: payments.map(p => ({
          payment_id: p.payment_id || String(Date.now()),
          mode: p.mode || 'Cash',
          amount: parseFloat(p.amount) || parseFloat(p.inr) || 0, // Backend expects 'amount', not 'inr'
          fine_wt: parseFloat(p.fine_wt) || parseFloat(p.fine_grams) || 0,
          fine_grams: parseFloat(p.fine_grams) || parseFloat(p.fine_wt) || 0, // Also send fine_grams for Old Gold
          date: p.date || bookingDate,
          note: p.reference || p.note || null,
          reference: p.reference || p.note || null,
          transaction_type: p.transaction_type || 'PAYMENT',
          direction: p.direction || 'IN',
          timestamp: p.timestamp || new Date().toISOString(),
          // Include detailed Old Gold items
          items: p.items || [],
          // Include other payment-specific fields
          card_last4: p.card_last4 || null,
          pos_ref: p.pos_ref || null,
          upi_ref: p.upi_ref || null,
          upi_id: p.upi_id || null,
          upi_handle: p.upi_handle || null,
          utr_ref: p.utr_ref || null,
          bank_name: p.bank_name || null,
          payer_acct_last4: p.payer_acct_last4 || null,
          cheque_no: p.cheque_no || null,
          cheque_bank: p.cheque_bank || null,
          cheque_date: p.cheque_date || null,
          cheque_status: p.cheque_status || null,
          gross_wt: p.gross_wt || null,
          net_wt: p.net_wt || null,
          stone_wt: p.stone_wt || null,
          purity: p.purity || null,
          rate_per_gram: p.rate_per_gram || null,
          total_value: p.total_value || null,
          metal_type: p.metal_type || null,
          deposits: p.deposits || []
        })),
        reminder_date: reminderEnabled && reminderDate ? reminderDate : (existingBooking?.reminder_date || null),
        
        // Send full payment reminder data to backend
        // Send enabled: false when reminder is disabled to trigger deletion
        payment_reminder_data: paymentReminderData 
          ? (paymentReminderData.enabled 
              ? {
                  enabled: true,
                  due_at: paymentReminderData.due_at,
                  amount_due: paymentReminderData.amount_due,
                  channels: paymentReminderData.channels || [],
                  reason_tags: paymentReminderData.reason_tags || [],
                  notes: paymentReminderData.notes || '',
                  auto_snooze: paymentReminderData.auto_snooze || null
                }
              : { enabled: false }
            )
          : null,
        
        reminder_enabled: reminderEnabled ?? existingBooking?.reminder_enabled ?? false,
        management_signature: uploadedManagementSig,
        management_signature_name: signatureData?.management_signature_name || existingBooking?.management_signature_name || null,
        customer_signature: uploadedCustomerSig
      };
      
      console.log('Sending booking data:', bookingData);
      
      let res;
      if (editMode && existingBooking) {
        res = await axiosInstance.put(`/bookings/${existingBooking.booking_id}`, bookingData);
        
        // Check if changes require approval (staff editing approved booking)
        if (res.data.requires_approval) {
          toast.success('✓ Changes submitted for admin approval', {
            duration: 5000,
            description: 'An admin will review your changes before they are applied.'
          });
          navigate('/my-submissions');
        } else {
          toast.success('Booking updated successfully!');
          navigate(`/bookings/${existingBooking.booking_id}`);
        }
      } else {
        res = await axiosInstance.post('/bookings', bookingData);
        
        // All users: Redirect to booking detail page
        toast.success('✓ Booking created successfully!', {
          duration: 3000,
          description: user?.role !== 'Admin' && user?.role !== 'Manager' 
            ? 'Booking submitted for admin approval. You can view, edit, or print your booking.'
            : 'You can view, edit, or print your booking.'
        });
        navigate(`/bookings/${res.data.booking_id}`);
      }
    } catch (err) {
      console.error('Booking creation error:', err.response?.data);
      
      // Handle different error formats
      let errorMessage = 'Error creating booking';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // If detail is an array (validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => `${e.loc?.join('.') || 'Error'}: ${e.msg}`).join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // If detail is an object
        else {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
      setShowSignatureModal(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    // Navigation already handled in handleSubmit - just close modal
  };

  const { subtotal, advancePaid, metalAdvance, totalAdvance, balance, advancePercent } = calculateTotals();

  // Booking type options
  // Use booking types from backend
  const bookingTypeOptions = bookingTypes.map(type => type.name);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-32">
      {/* Full-screen Loading Overlay during Booking Creation */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600 mx-auto mb-4"></div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Creating Booking...</h3>
            <p className="text-gray-600 text-sm">Please wait while we upload signatures and create your booking.</p>
            <p className="text-xs text-gray-400 mt-3">This may take a few seconds</p>
          </div>
        </div>
      )}

      {/* Sticky Header with Stepper */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b-2 border-amber-200">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <button 
              onClick={() => confirmNavigation(() => navigate(-1))} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
              title="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-bold text-gray-800">
              {editMode ? `Edit Booking - ${existingBooking?.booking_id}` : 'Create Booking'}
            </h1>
          </div>
          
          {/* Mini Stepper */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            <button
              onClick={() => scrollToSection(itemsRef, 'items')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.items ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <span>Items ({bookingItems.length})</span>
              {getStatusIcon(getSectionStatus('items'))}
            </button>
            
            <button
              onClick={() => scrollToSection(ratesRef, 'rates')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.rates ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <span>Rates</span>
              {getStatusIcon(getSectionStatus('rates'))}
            </button>
            
            <button
              onClick={() => scrollToSection(paymentRef, 'payment')}
              className={`px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-2 transition-all ${
                expandedSections.payment ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <span>Payment</span>
              {getStatusIcon(getSectionStatus('payment'))}
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {/* Merged Header - Customer & Booking Date */}
        <div className="premium-card border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Booking Date */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Booking Date *</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    className="input-field text-sm flex-1"
                    value={bookingDate}
                    onChange={(e) => handleBookingDateChange(e.target.value)}
                  />
                  {bookingDate < new Date().toISOString().split('T')[0] && (
                    <span className="px-2 py-1 bg-amber-200 text-amber-800 text-xs font-semibold rounded whitespace-nowrap">
                      Back-dated
                    </span>
                  )}
                </div>
              </div>
              
              {/* Customer Selector */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Customer *</label>
                {selectedCustomer ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white border-2 border-green-200 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-gray-800">
                            {customers.find(c => c.id === selectedCustomer)?.name || 'Selected'}
                          </span>
                          {customers.find(c => c.id === selectedCustomer)?.mobile && (
                            <span className="text-xs text-gray-600 ml-2">
                              • {customers.find(c => c.id === selectedCustomer)?.mobile}
                            </span>
                          )}
                          {customers.find(c => c.id === selectedCustomer)?.alternate_mobile && (
                            <span className="text-xs text-gray-500 ml-1">
                              / {customers.find(c => c.id === selectedCustomer)?.alternate_mobile}
                            </span>
                          )}
                          {customers.find(c => c.id === selectedCustomer)?.email && (
                            <div className="text-xs text-gray-600 mt-1 break-all">
                              ✉️ {customers.find(c => c.id === selectedCustomer)?.email}
                            </div>
                          )}
                          {customers.find(c => c.id === selectedCustomer)?.address && (
                            <div className="text-xs text-gray-600 mt-1 break-words">
                              📍 {customers.find(c => c.id === selectedCustomer)?.address}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const customer = customers.find(c => c.id === selectedCustomer);
                              if (customer) {
                                setEditingCustomer(customer);
                                setShowEditCustomerModal(true);
                              }
                            }}
                            className="text-xs text-blue-600 font-semibold hover:text-blue-700"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowCustomerModal(true)}
                            className="text-xs text-amber-600 font-semibold hover:text-amber-700"
                          >
                            Change
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowCustomerModal(true)}
                    className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    Select Customer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 1: BOOKING ITEMS */}
        <div className="premium-card border-2 border-amber-200" ref={itemsRef}>
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('items')}
          >
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-800">Booking Items</h2>
              <span className="bg-white text-amber-600 font-bold text-xs px-2 py-1 rounded-full">{bookingItems.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  addBookingItem();
                }}
                className="text-xs font-semibold text-amber-700 hover:text-amber-900 flex items-center gap-1 px-2 py-1 bg-white rounded-lg"
              >
                <Plus className="w-3 h-3" />
                Add Item
              </button>
              {expandedSections.items ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>

          {expandedSections.items && (
            <div className="p-4 space-y-3">
              {bookingItems.length === 0 ? (
                <div className="text-center py-8 bg-amber-50 rounded-xl">
                  <p className="text-sm text-gray-600">No booking items yet. Click &quot;+ Add Item&quot; above</p>
                </div>
              ) : (
                bookingItems.map((item, itemIndex) => (
                  <div key={item.id} className="space-y-2">
                    <div className="border-2 border-amber-200 rounded-xl overflow-hidden bg-white">
                      {/* Collapsed Preview */}
                      {!expandedItems[item.id] ? (
                        <div className="p-3 bg-amber-50">
                          <div className="flex justify-between items-start mb-1">
                            <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full font-bold">#{itemIndex + 1}</span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleItemExpand(item.id)}
                                className="p-1 hover:bg-amber-100 rounded"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeBookingItem(item.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap text-xs leading-relaxed">
                              <span className="font-semibold text-gray-900">{item.booking_type}</span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-700">{item.weight || 0}g</span>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-700">{item.purity}</span>
                              {item.public_remark && (
                                <>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-600 italic">
                                    {item.public_remark.length > 30 ? `${item.public_remark.substring(0, 30)}...` : item.public_remark}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Expanded Header */}
                          <div className="p-3 bg-amber-50 flex justify-between items-center">
                            <div className="flex items-center gap-2 flex-1">
                              <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full font-bold">#{itemIndex + 1}</span>
                              <div>
                                <h4 className="font-semibold text-sm">{item.booking_type}</h4>
                                <p className="text-xs text-gray-600">{item.weight || 0}g</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleItemExpand(item.id)}
                                className="p-1"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeBookingItem(item.id)}
                                className="p-1 text-red-500"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Expanded Form */}
                          <div className="p-3 space-y-3 text-sm">
                            {/* Booking Type */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Booking Type</label>
                              <button
                                type="button"
                                onClick={() => {
                                  setBookingTypeModalTargetId(item.id);
                                  setShowBookingTypeModal(true);
                                }}
                                className="input-field w-full text-sm text-left flex items-center justify-between hover:bg-amber-50 transition-colors"
                              >
                                <span>{item.booking_type}</span>
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              </button>
                            </div>

                            {/* Weight */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Weight (g) *</label>
                              <input
                                type="number"
                                step="0.01"
                                className="input-field w-full text-sm"
                                value={item.weight || ''}
                                onChange={(e) => updateBookingItem(item.id, 'weight', e.target.value)}
                                placeholder="Enter weight in grams"
                              />
                            </div>

                            {/* Public Remark */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Public Remark</label>
                              <textarea
                                className="input-field w-full text-sm"
                                rows="2"
                                value={item.public_remark}
                                onChange={(e) => updateBookingItem(item.id, 'public_remark', e.target.value)}
                                placeholder="Customer-facing notes"
                              />
                            </div>

                            {/* Internal Remark */}
                            <div>
                              {!showInternalRemark[item.id] ? (
                                <button
                                  type="button"
                                  onClick={() => toggleInternalRemark(item.id)}
                                  className="text-xs font-semibold text-gray-600 flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Internal Remark
                                </button>
                              ) : (
                                <div>
                                  <label className="block text-xs font-semibold text-gray-700 mb-1">Internal Remark</label>
                                  <textarea
                                    className="input-field w-full text-sm bg-yellow-50"
                                    rows="2"
                                    value={item.internal_remark}
                                    onChange={(e) => updateBookingItem(item.id, 'internal_remark', e.target.value)}
                                    placeholder="Internal-only notes"
                                  />
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => collapseItem(item.id)}
                              className="btn-secondary w-full text-sm py-2"
                            >
                              Save & Collapse
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Quick Add Button */}
                    {!expandedItems[item.id] && itemIndex === bookingItems.length - 1 && (
                      <button
                        type="button"
                        onClick={() => addBookingItem()}
                        className="w-full py-2.5 text-xs font-semibold text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg flex items-center justify-center gap-1 transition-colors border-2 border-dashed border-amber-300"
                      >
                        <Plus className="w-4 h-4" />
                        Add Another Booking Item
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* SECTION 2: RATES */}
        <div ref={ratesRef} className="premium-card border-2 border-amber-200">
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('rates')}
          >
            <h2 className="text-base font-bold text-gray-800">Rates & Policy</h2>
            {expandedSections.rates ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>

          {expandedSections.rates && (
            <RatesSection
              taxScope={taxScope}
              setTaxScope={setTaxScope}
              gstApplicable={gstApplicable}
              setGstApplicable={setGstApplicable}
              gstInputStyle={gstInputStyle}
              setGstInputStyle={setGstInputStyle}
              gstPercent={gstPercent}
              rateMode={rateMode}
              setRateMode={setRateMode}
              ratePolicy={ratePolicy}
              setRatePolicy={setRatePolicy}
              lockDate={lockDate}
              setLockDate={setLockDate}
              base24kRate={base24kRate}
              handleBase24kChange={handleBase24kChange}
              purityRates={purityRates}
              handlePurityRateChange={handlePurityRateChange}
              purityRateLinks={purityRateLinks}
              togglePurityLink={togglePurityLink}
              purityMultipliers={purityMultipliers}
              items={bookingItems}
              itemRates={itemRates}
              setItemRates={setItemRates}
            />
          )}
        </div>

        {/* SECTION 3: PAYMENT */}
        <div ref={paymentRef} className="premium-card border-2 border-amber-200">
          <div
            className="p-4 bg-gradient-to-r from-amber-100 to-orange-100 cursor-pointer flex justify-between items-center"
            onClick={() => toggleSection('payment')}
          >
            <h2 className="text-base font-bold text-gray-800">Payment & Advance</h2>
            {expandedSections.payment ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>

          {expandedSections.payment && (
            <div className="p-4 space-y-3">
              {/* Payment Summary */}
              <PaymentSummary
                payments={payments}
                metalCreditBalance={metalCreditBalance}
                subtotal={subtotal}
                onEdit={handlePaymentEdit}
                onDelete={handlePaymentDelete}
              />

              {/* Add Payment Button */}
              <button
                onClick={() => setShowPaymentTypeSelector(true)}
                className="w-full py-3 bg-gradient-to-r from-green-400 to-emerald-400 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Payment
              </button>

              {/* Totals Display */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Subtotal:</span>
                    <span className="font-bold text-gray-900">₹{subtotal.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-green-700">
                    <span>Cash Advance:</span>
                    <span className="font-semibold">₹{advancePaid.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-amber-700">
                    <span>Metal Credit:</span>
                    <span className="font-semibold">{metalAdvance.toFixed(3)}g</span>
                  </div>
                  <div className="border-t-2 border-amber-300 pt-2 flex justify-between">
                    <span className="font-semibold text-gray-700">Balance:</span>
                    <span className="font-bold text-lg text-red-600">₹{balance.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="text-center">
                    <span className="text-xs bg-amber-200 text-amber-800 px-3 py-1 rounded-full font-semibold">
                      {advancePercent}% Advance Paid
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Reminder Card */}
              <PaymentReminderCard
                isExpanded={showPaymentReminder}
                onToggle={() => setShowPaymentReminder(!showPaymentReminder)}
                advancePaid={advancePaid}
                requiredAdvance={subtotal * 0.8}
                selectedCustomer={selectedCustomer}
                customers={customers}
                orderDate={bookingDate}
                onReminderChange={setPaymentReminderData}
                initialData={paymentReminderData}
              />

              {/* Signatures Section - Same as Order Form */}
              <div className="bg-white rounded-xl shadow-md p-6 mt-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">✍️ Signatures</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Management Signature */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Management Signature
                    </label>
                    {managementSignature ? (
                      <div className="border-2 border-green-300 rounded-xl p-3 bg-green-50">
                        <img 
                          src={managementSignature} 
                          alt="Management Signature" 
                          className="w-full h-24 object-contain mb-2"
                        />
                        <button
                          onClick={() => setManagementSignature(null)}
                          className="w-full py-1 bg-red-100 text-red-600 text-sm rounded-lg hover:bg-red-200"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowManagementSignatureModal(true)}
                        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-colors"
                      >
                        ✍️ Tap to Sign (Management)
                      </button>
                    )}
                  </div>

                  {/* Customer Signature */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Customer Signature
                    </label>
                    {customerSignature ? (
                      <div className="border-2 border-green-300 rounded-xl p-3 bg-green-50 mt-8">
                        <img 
                          src={customerSignature} 
                          alt="Customer Signature" 
                          className="w-full h-24 object-contain mb-2"
                        />
                        <button
                          onClick={() => setCustomerSignature(null)}
                          className="w-full py-1 bg-red-100 text-red-600 text-sm rounded-lg hover:bg-red-200"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowCustomerSignatureModal(true)}
                        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-colors mt-8"
                      >
                        ✍️ Tap to Sign (Customer)
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Review & Submit Button */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-amber-200 p-4 shadow-lg z-30">
          <button
            onClick={() => setShowReviewPage(true)}
            disabled={!selectedCustomer || bookingItems.length === 0}
            className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Review & Finalize Booking →
          </button>
        </div>
      </div>

      {/* Modals */}
      {showCustomerModal && (
        <CustomerSelectionModal
          onClose={() => setShowCustomerModal(false)}
          customers={customers}
          onCustomerSelected={(customer) => {
            if (!customers.find(c => c.id === customer.id)) {
              setCustomers([...customers, customer]);
            }
            setSelectedCustomer(customer.id || customer.customer_id);
            setShowCustomerModal(false);
            toast.success('Customer selected!');
          }}
          selectedCustomerId={selectedCustomer}
        />
      )}

      {/* Booking Type Modal */}
      {showBookingTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-2xl sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b-2 border-amber-200 flex justify-between items-center bg-gradient-to-r from-amber-100 to-orange-100">
              <h3 className="text-lg font-bold text-gray-800">Select Booking Type</h3>
              <button 
                onClick={() => {
                  setShowBookingTypeModal(false);
                  setBookingTypeSearch('');
                  setBookingTypeModalTargetId(null);
                }}
                className="p-2 hover:bg-amber-200 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  className="input-field w-full pl-10"
                  placeholder="Search booking types..."
                  value={bookingTypeSearch}
                  onChange={(e) => setBookingTypeSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {bookingTypeOptions
                  .filter(type => type.toLowerCase().includes(bookingTypeSearch.toLowerCase()))
                  .map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        if (bookingTypeModalTargetId) {
                          updateBookingItem(bookingTypeModalTargetId, 'booking_type', type);
                        }
                        setShowBookingTypeModal(false);
                        setBookingTypeSearch('');
                        setBookingTypeModalTargetId(null);
                      }}
                      className="p-4 border-2 border-amber-200 rounded-lg hover:bg-amber-50 transition-all hover:border-amber-400 hover:shadow-md"
                    >
                      <div className="text-sm font-semibold text-gray-800">{type}</div>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <PurityPickerModal
        isOpen={activeModal === 'purity'}
        onClose={() => {
          setActiveModal(null);
          setModalItemId(null);
        }}
        onSelect={(value) => {
          if (modalItemId) {
            updateBookingItem(modalItemId, 'purity', value);
          }
        }}
        currentValue={modalItemId ? bookingItems.find(i => i.id === modalItemId)?.purity : '22K'}
        purities={Object.keys(purityMultipliers)}
      />

      {/* Payment Modals */}
      <PaymentTypeSelector
        isOpen={showPaymentTypeSelector}
        onClose={() => setShowPaymentTypeSelector(false)}
        onSelectType={(type) => {
          setShowPaymentTypeSelector(false);
          setActivePaymentModal(type);
        }}
      />

      <CashPaymentModal
        isOpen={activePaymentModal === 'cash'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <CardPaymentModal
        isOpen={activePaymentModal === 'card'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <UPIPaymentModal
        isOpen={activePaymentModal === 'upi'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <BankTransferModal
        isOpen={activePaymentModal === 'bank'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      <OldGoldModal
        isOpen={activePaymentModal === 'old-gold'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? {
          ...payments[editingPaymentIndex],
          items: payments[editingPaymentIndex]?.items || [] // Ensure items array exists
        } : null}
      />

      <MetalCreditModal
        isOpen={activePaymentModal === 'metal'}
        onClose={() => {
          setActivePaymentModal(null);
          setEditingPaymentIndex(null);
        }}
        onSave={handlePaymentSave}
        editData={editingPaymentIndex !== null ? payments[editingPaymentIndex] : null}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Delete Payment?</h3>
            <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete this payment entry?</p>
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Page */}
      {showReviewPage && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          {/* Header - Sticky */}
          <div className="sticky top-0 bg-gradient-to-r from-purple-100 to-pink-100 border-b-2 border-purple-200 p-3 sm:p-4 shadow-md z-10">
            <div className="flex items-center justify-between max-w-3xl mx-auto">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">Review Booking</h2>
                <p className="text-xs text-gray-600 mt-0.5">Please verify all details before finalizing</p>
              </div>
              <button 
                onClick={() => setShowReviewPage(false)}
                className="p-2 hover:bg-purple-200 rounded-lg transition-colors"
                aria-label="Close review"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>

          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-w-3xl mx-auto pb-6">
            {/* Customer & Date Info */}
            <div className="bg-white rounded-xl shadow-md border-2 border-purple-200 p-3 sm:p-4">
              <h3 className="text-sm font-bold text-purple-800 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Booking Information
              </h3>
              <div className="space-y-2 sm:space-y-3 text-sm">
                <div className="bg-purple-50 rounded-lg p-2 sm:p-3">
                  <p className="text-gray-600 text-xs mb-1">Customer</p>
                  <p className="font-semibold text-base">{customers.find(c => c.id === selectedCustomer)?.name || 'Not selected'}</p>
                  <div className="mt-1 space-y-0.5">
                    {customers.find(c => c.id === selectedCustomer)?.mobile && (
                      <p className="text-xs text-gray-600 flex items-center gap-1">
                        📱 {customers.find(c => c.id === selectedCustomer)?.mobile}
                      </p>
                    )}
                    {customers.find(c => c.id === selectedCustomer)?.alternate_mobile && (
                      <p className="text-xs text-gray-600">Alt: {customers.find(c => c.id === selectedCustomer)?.alternate_mobile}</p>
                    )}
                    {customers.find(c => c.id === selectedCustomer)?.email && (
                      <p className="text-xs text-gray-600 flex items-center gap-1">
                        ✉️ {customers.find(c => c.id === selectedCustomer)?.email}
                      </p>
                    )}
                    {customers.find(c => c.id === selectedCustomer)?.address && (
                      <p className="text-xs text-gray-600 break-words">
                        📍 {customers.find(c => c.id === selectedCustomer)?.address}
                      </p>
                    )}
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2 sm:p-3">
                  <p className="text-gray-600 text-xs mb-1">Booking Date</p>
                  <p className="font-semibold flex items-center gap-2 flex-wrap">
                    <span>{new Date(bookingDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    {bookingDate < new Date().toISOString().split('T')[0] && (
                      <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded-full font-bold">Back-dated</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Booking Items Summary */}
            <div className="bg-white rounded-xl shadow-md border-2 border-amber-200 p-3 sm:p-4">
              <h3 className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Booking Items ({bookingItems.length})
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {bookingItems.map((item, idx) => (
                  <div key={item.id} className="bg-amber-50 border-2 border-amber-200 rounded-lg p-2 sm:p-3 text-xs">
                    {/* Header with Item Number */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="bg-amber-600 text-white px-2 py-0.5 rounded-full font-bold text-xs">#{idx + 1}</span>
                        <span className="font-semibold text-sm">{item.booking_type}</span>
                      </div>
                    </div>

                    {/* Item Details Grid - Responsive */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-2">
                      <div>
                        <span className="text-gray-600">Weight:</span> 
                        <span className="font-medium ml-1">{item.weight}g</span>
                      </div>
                      
                      {/* Public Remarks */}
                      {item.public_remark && (
                        <div className="col-span-2">
                          <span className="text-gray-600">Public Remark:</span> 
                          <span className="font-medium ml-1">{item.public_remark}</span>
                        </div>
                      )}
                    </div>

                    {/* Internal Remarks - Special styling */}
                    {item.internal_remark && (
                      <div className="border-t border-amber-300 pt-2 mt-2 bg-yellow-50 -m-3 p-3 rounded-b-lg">
                        <p className="text-gray-600 font-semibold text-xs mb-1">🔒 Internal Notes (Staff Only):</p>
                        <p className="font-medium text-red-700">{item.internal_remark}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Total Weight Summary */}
              <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-amber-300 bg-amber-100 -mx-2 sm:-mx-3 -mb-2 sm:-mb-3 px-2 sm:px-3 py-2 sm:py-3 rounded-b-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">Total Weight:</span>
                  <span className="text-base sm:text-lg font-bold text-amber-700">
                    {bookingItems.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0).toFixed(2)}g
                  </span>
                </div>
              </div>
            </div>

            {/* Rates Info */}
            {ratePolicy !== 'none' && (
              <div className="bg-white rounded-xl shadow-md border-2 border-blue-200 p-3 sm:p-4">
                <h3 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Rates & Policy
                </h3>
                <div className="bg-blue-50 rounded-lg p-2 sm:p-3 space-y-2 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Rate Policy:</span>
                    <span className="font-bold text-blue-700 text-sm">
                      {ratePolicy === 'lock-today' ? '🔒 Locked Today' : '📅 Rate at Delivery'}
                    </span>
                  </div>
                  {rateMode === 'single' && base24kRate && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">24K Base Rate:</span>
                        <span className="font-semibold text-blue-700">₹{parseFloat(base24kRate).toLocaleString('en-IN')}/g</span>
                      </div>
                      {lockDate && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rate Locked On:</span>
                          <span className="font-medium text-blue-600">
                            {new Date(lockDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex justify-between pt-2 border-t border-blue-200">
                    <span className="text-gray-600">GST:</span>
                    <span className="font-medium">
                      {gstApplicable ? `${gstPercent}% (${gstInputStyle === 'INCL' ? 'Incl.' : 'Excl.'})` : 'Kaccha'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Summary */}
            {payments.length > 0 && (
              <div className="bg-white rounded-xl shadow-md border-2 border-green-200 p-3 sm:p-4">
                <h3 className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Payment Details ({payments.length})
                </h3>
                <div className="space-y-2">
                  {payments.map((payment, idx) => {
                    const isRefund = payment.transaction_type === 'REFUND';
                    return (
                    <div key={idx} className={`border-2 rounded-lg p-2 sm:p-3 text-xs ${isRefund ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex justify-between items-center mb-1">
                        <span className={`font-bold ${isRefund ? 'text-red-700' : 'text-green-700'}`}>
                          {isRefund ? '↩️ REFUND - ' : ''}#{idx + 1} {payment.mode}
                        </span>
                        {payment.date && (
                          <span className="text-gray-600 text-xs">
                            {new Date(payment.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        {payment.amount && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Cash Amount:</span>
                            <span className={`font-semibold ${isRefund ? 'text-red-700' : 'text-green-700'}`}>
                              {isRefund ? '-' : ''}₹{parseFloat(payment.amount).toLocaleString('en-IN')}
                            </span>
                          </div>
                        )}
                        {payment.fine_wt && parseFloat(payment.fine_wt) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Metal Credit:</span>
                            <span className="font-semibold text-amber-700">{payment.fine_wt}g fine</span>
                          </div>
                        )}
                        {payment.reference && (
                          <div className="text-gray-600 text-xs truncate">
                            Ref: {payment.reference}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
                
                {/* Payment Totals */}
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t-2 border-green-300 bg-green-100 -mx-3 sm:-mx-4 -mb-3 sm:-mb-4 px-3 sm:px-4 py-2 sm:py-3 rounded-b-xl">
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-700">Cash Advance:</span>
                      <span className="font-bold text-green-700">₹{advancePaid.toLocaleString('en-IN')}</span>
                    </div>
                    {metalAdvance > 0 && (
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">Metal Credit:</span>
                        <span className="font-bold text-amber-700">{metalAdvance.toFixed(3)}g</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Advance Status */}
            {subtotal > 0 && (
              <div className="bg-white rounded-xl shadow-md border-2 border-purple-200 p-3 sm:p-4">
                <h3 className="text-sm font-bold text-purple-800 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Advance Status
                </h3>
                <div className="bg-purple-50 rounded-lg p-2 sm:p-3 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal (Approx):</span>
                    <span className="font-semibold">₹{subtotal.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Advance Paid:</span>
                    <span className="font-bold text-green-700">₹{advancePaid.toLocaleString('en-IN')}</span>
                  </div>
                  {metalAdvance > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Metal Credit:</span>
                      <span className="font-bold text-amber-700">{metalAdvance.toFixed(3)}g</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-purple-200">
                    <span className="text-gray-600 font-semibold">Balance Due:</span>
                    <span className="font-bold text-red-600">₹{balance.toLocaleString('en-IN')}</span>
                  </div>
                  
                  {/* Advance Percentage Indicator */}
                  <div className="pt-2 border-t border-purple-200">
                    <div className="flex justify-between mb-1.5">
                      <span className="text-gray-600 font-semibold">Advance:</span>
                      <span className={`font-bold text-sm ${parseFloat(advancePercent) >= 80 ? 'text-green-600' : 'text-orange-600'}`}>
                        {advancePercent}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className={`h-2.5 rounded-full transition-all ${parseFloat(advancePercent) >= 80 ? 'bg-green-500' : 'bg-orange-500'}`}
                        style={{ width: `${Math.min(parseFloat(advancePercent), 100)}%` }}
                      ></div>
                    </div>
                    {parseFloat(advancePercent) < 80 && (
                      <p className="text-xs text-orange-600 mt-1.5">⚠️ Minimum 80% advance recommended</p>
                    )}
                    {parseFloat(advancePercent) >= 80 && (
                      <p className="text-xs text-green-600 mt-1.5">✓ Sufficient advance received</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Signatures Display */}
            <div className="bg-white rounded-xl shadow-md border-2 border-indigo-200 p-3 sm:p-4">
              <h3 className="text-sm font-bold text-indigo-800 mb-3 flex items-center gap-2">
                <Edit3 className="w-4 h-4" />
                Signatures
              </h3>
              
              {managementSignature || customerSignature ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {managementSignature && (
                    <div>
                      <p className="text-xs text-gray-600 mb-1.5 font-semibold">Management</p>
                      <div className="bg-white rounded-lg border-2 border-indigo-300 p-2">
                        <img src={managementSignature} alt="Management" className="w-full h-20 object-contain" />
                        {managementSignatureName && (
                          <p className="text-xs text-center text-gray-700 mt-1.5 font-semibold bg-indigo-50 py-1 rounded">{managementSignatureName}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {customerSignature && (
                    <div>
                      <p className="text-xs text-gray-600 mb-1.5 font-semibold">Customer</p>
                      <div className="bg-white rounded-lg border-2 border-indigo-300 p-2">
                        <img src={customerSignature} alt="Customer" className="w-full h-20 object-contain" />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">No signatures added yet</p>
              )}
              
              <button 
                onClick={() => setShowReviewPage(false)}
                className="mt-3 w-full sm:w-auto text-xs sm:text-sm text-indigo-600 font-semibold hover:text-indigo-700 hover:bg-indigo-50 py-2 px-4 rounded-lg transition-colors"
              >
                {managementSignature || customerSignature ? 'Edit Signatures' : 'Add Signatures'}
              </button>
            </div>

            {/* Action Buttons - Non-sticky, capsule style */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200">
              <div className="flex items-center justify-center gap-3 max-w-2xl mx-auto">
                <button
                  onClick={() => setShowReviewPage(false)}
                  className="px-6 py-2.5 bg-white border-2 border-amber-300 text-amber-700 rounded-full font-semibold hover:bg-amber-50 transition-colors flex items-center gap-2 text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Edit
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Finalize & Sign
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEditCustomerModal && editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
          }}
          onCustomerUpdated={() => {
            setShowEditCustomerModal(false);
            setEditingCustomer(null);
            loadCustomers();
          }}
        />
      )}

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          onClose={() => setShowSignatureModal(false)}
          onConfirm={handleSignaturesComplete}
        />
      )}

      {/* Management Signature Modal */}
      <SignatureModal
        isOpen={showManagementSignatureModal}
        onClose={() => setShowManagementSignatureModal(false)}
        onSave={(signatureData) => {
          setManagementSignature(signatureData);
          setShowManagementSignatureModal(false);
        }}
        title="Management Signature"
        existingSignature={managementSignature}
      />

      {/* Customer Signature Modal */}
      <SignatureModal
        isOpen={showCustomerSignatureModal}
        onClose={() => setShowCustomerSignatureModal(false)}
        onSave={(signatureData) => {
          setCustomerSignature(signatureData);
          setShowCustomerSignatureModal(false);
        }}
        title="Customer Signature"
        existingSignature={customerSignature}
      />

      {/* Success Modal */}
      {showSuccessModal && createdBookingData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Booking Created Successfully!</h2>
            <p className="text-gray-600 mb-1">Booking ID: <span className="font-semibold">{createdBookingData.booking_id}</span></p>
            <p className="text-gray-600 mb-2">Customer: <span className="font-semibold">{createdBookingData.customer_name}</span></p>
            <p className="text-sm text-gray-500 mb-6">What would you like to do next?</p>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/submissions/my-all');
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Go to My Submissions
              </button>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/');
                }}
                className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateBookingStepper;
